import React, { useState } from "react";
import { Pressable, StyleSheet, View, Image } from "react-native";
import { Circle, CircleCheck, EllipsisVertical, RotateCcw, Trash2 } from "lucide-react";
import { Menu } from "react-native-paper";
import { colors, radii, spacing, shadowStyles } from "../../../styles/design-system";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import Pill from "../../common/Pill/Pill";
import { formatTimeAgo } from "../../../utils/date";

export interface Objective {
  id: string;
  objective: string;
  sequence?: number;
  status?: string;
}

export type ActivityStatus = "not_started" | "in_progress" | "completed";

export interface CompanionILECardProps {
  id: string;
  title: string;
  courseName?: string;
  created?: Date | string;
  description?: string;
  thumbnail?: string;
  objectives?: Objective[];
  status?: ActivityStatus;
  onPress?: () => void;
  onResetProgress?: () => void;
  onDeleteActivity?: () => void;
}

const CompanionILECard: React.FC<CompanionILECardProps> = ({
  title,
  courseName,
  created,
  description,
  thumbnail,
  objectives = [],
  status = "not_started",
  onPress,
  onResetProgress,
  onDeleteActivity,
}) => {
  const [menuVisible, setMenuVisible] = useState(false);

  const openMenu = () => setMenuVisible(true);
  const closeMenu = () => setMenuVisible(false);

  const handleResetProgress = () => {
    closeMenu();
    onResetProgress?.();
  };

  const handleDeleteActivity = () => {
    closeMenu();
    onDeleteActivity?.();
  };

  // Sort objectives by sequence and take first 3
  const sortedObjectives = [...objectives].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  const displayedObjectives = sortedObjectives.slice(0, 3);
  const remainingCount = objectives.length - displayedObjectives.length;

  // Check if objective is completed
  const isObjectiveCompleted = (objective: Objective) => {
    return objective.status === "completed";
  };

  // Get status badge config
  const getStatusBadgeConfig = () => {
    switch (status) {
      case "completed":
        return {
          text: "Completed",
          backgroundColor: "#F0FDF4",
          textColor: colors.grey[700],
          borderColor: "#BBF7D0",
        };
      case "in_progress":
        return {
          text: "In Progress",
          backgroundColor: "#E6EBFF",
          textColor: colors.grey[700],
          borderColor: "#9AAFFE",
        };
      case "not_started":
      default:
        return {
          text: "Not Started",
          backgroundColor: "#F9FAFB",
          textColor: colors.grey[700],
          borderColor: "#EAECF0",
        };
    }
  };

  // Get button text based on status
  const getButtonText = () => {
    switch (status) {
      case "completed":
        return "Review Learning Activity";
      case "in_progress":
        return "Continue Learning Activity";
      case "not_started":
      default:
        return "Start Learning Activity";
    }
  };

  const statusBadgeConfig = getStatusBadgeConfig();

  return (
    <View style={styles.cardContainer}>
      {/* Image Section with Badge and Menu */}
      <View style={styles.imageContainer}>
        {thumbnail ? (
          <Image
            source={{ uri: thumbnail }}
            style={styles.image}
            resizeMode="cover"
            accessibilityLabel={`${title} thumbnail`}
            alt="Activity thumbnail"
          />
        ) : (
          <View style={styles.placeholderImage} />
        )}

        {/* Status Badge */}
        <View style={styles.badgeContainer}>
          <Pill
            text={statusBadgeConfig.text}
            backgroundColor={statusBadgeConfig.backgroundColor}
            textColor={statusBadgeConfig.textColor}
            textSize="xs"
            borderColor={statusBadgeConfig.borderColor}
            borderWidth={1}
          />
        </View>

        {/* Menu */}
        <View style={styles.menuContainer}>
          <Menu
            visible={menuVisible}
            onDismiss={closeMenu}
            anchor={
              <Pressable
                onPress={openMenu}
                style={styles.menuButton}
                accessibilityLabel="Open activity options menu"
                accessibilityRole="button">
                <EllipsisVertical size={20} color={colors.grey[700]} />
              </Pressable>
            }
            anchorPosition="bottom"
            contentStyle={styles.menuContent}>
            <Menu.Item
              onPress={handleResetProgress}
              title="Reset Progress"
              leadingIcon={() => <RotateCcw size={14} color="#0C111D" />}
              titleStyle={{ color: "#0C111D", fontSize: 14 }}
            />
            <Menu.Item
              onPress={handleDeleteActivity}
              title="Delete Activity"
              leadingIcon={() => <Trash2 size={14} color="#DC2626" />}
              titleStyle={{ color: "#DC2626", fontSize: 14 }}
            />
          </Menu>
        </View>
      </View>

      {/* Details Section */}
      <View style={styles.detailsContainer}>
        {/* Title */}
        <View style={styles.titleContainer}>
          <CustomText
            text={title}
            size="l"
            weight="bold"
            style={styles.title}
            useTranslationText={false}
            numberOfLines={2}
          />
        </View>

        {/* Course and Created Date */}
        {(courseName || created) && (
          <View style={styles.metaRow}>
            {courseName && <CustomText text={courseName} size="s" style={styles.metaText} useTranslationText={false} />}
            {courseName && created && (
              <CustomText text="•" size="s" style={styles.separator} useTranslationText={false} />
            )}
            {created && (
              <CustomText text={formatTimeAgo(created)} size="s" style={styles.metaText} useTranslationText={false} />
            )}
          </View>
        )}

        {/* Description */}
        {description && (
          <CustomText
            text={description}
            size="s"
            style={styles.description}
            useTranslationText={false}
            numberOfLines={2}
          />
        )}

        {/* Learning Goals */}
        {objectives.length > 0 && (
          <View style={styles.learningGoalsContainer}>
            <View style={styles.learningGoalsHeader}>
              <CustomText
                text="LEARNING GOALS"
                size="xs"
                weight="bold"
                style={styles.learningGoalsTitle}
                useTranslationText={true}
              />
              <CustomText
                text={`${objectives.filter(isObjectiveCompleted).length}/${objectives.length}`}
                size="xs"
                weight="bold"
                style={styles.progressCount}
                useTranslationText={false}
              />
            </View>

            {displayedObjectives.map((objective) => {
              const completed = isObjectiveCompleted(objective);
              return (
                <View key={objective.id} style={styles.objectiveRow}>
                  {completed ? (
                    <CircleCheck size={16} color="#16A34A" style={styles.objectiveIcon} />
                  ) : (
                    <Circle size={16} color={colors.grey[400]} style={styles.objectiveIcon} />
                  )}
                  <CustomText
                    text={objective.objective}
                    size="s"
                    style={[styles.objectiveText, completed && styles.completedObjectiveText]}
                    useTranslationText={false}
                    numberOfLines={1}
                  />
                </View>
              );
            })}

            {remainingCount > 0 && (
              <CustomText
                text={`+${remainingCount} more`}
                size="s"
                style={styles.moreText}
                useTranslationText={false}
              />
            )}
          </View>
        )}

        {/* Action Button */}
        <CustomButton
          text={getButtonText()}
          onPress={onPress}
          styleType="primary"
          size="m"
          style={[styles.actionButton, { backgroundColor: "#9333EA" }]}
          useTranslationText={false}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: colors.white,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "hidden",
    ...shadowStyles.sm,
    maxWidth: 400,
  },
  imageContainer: {
    position: "relative",
    width: "100%",
    height: 160,
    backgroundColor: colors.grey[100],
  },
  image: {
    width: "100%",
    height: "100%",
  },
  placeholderImage: {
    width: "100%",
    height: "100%",
    backgroundColor: colors.grey[200],
  },
  badgeContainer: {
    position: "absolute",
    top: spacing.xs,
    left: spacing.xs,
  },
  menuContainer: {
    position: "absolute",
    top: spacing.xs,
    right: spacing.xs,
  },
  menuButton: {
    width: 32,
    height: 32,
    borderRadius: radii.full,
    backgroundColor: colors.white,
    alignItems: "center",
    justifyContent: "center",
    ...shadowStyles.xs,
  },
  menuContent: {
    backgroundColor: colors.white,
    borderRadius: radii.md,
  },
  detailsContainer: {
    padding: spacing.sm,
    gap: spacing.xs,
  },
  titleContainer: {
    minHeight: 56, // Ensures consistent height for 1-2 line titles (28px line height * 2)
  },
  title: {
    color: colors.grey[900],
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  metaText: {
    color: colors.grey[600],
  },
  separator: {
    color: colors.grey[400],
    marginHorizontal: spacing.xxs,
  },
  description: {
    color: colors.grey[700],
    marginTop: spacing.xxs,
  },
  learningGoalsContainer: {
    marginTop: spacing.xs,
    gap: spacing.xs,
    minHeight: 140, // Ensures consistent height even with fewer objectives
  },
  learningGoalsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  learningGoalsTitle: {
    color: colors.grey[700],
    letterSpacing: 0.5,
  },
  progressCount: {
    color: colors.grey[600],
  },
  objectiveRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.xs,
  },
  objectiveIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  objectiveText: {
    flex: 1,
    color: colors.grey[700],
  },
  completedObjectiveText: {
    textDecorationLine: "line-through",
    color: colors.grey[500],
  },
  moreText: {
    color: colors.grey[500],
    marginLeft: spacing.lg,
  },
  actionButton: {
    marginTop: spacing.xs,
    width: "100%",
  },
});

export default CompanionILECard;
